import React, { useEffect, useState, useMemo } from 'react'
import styles from './styles.module.css'
import { useUiStore } from '../../../../store/uiStore'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useAddToCart } from '../../../hooks/useAddToCart'
import useProductOffers from '../../../../hooks/useProductOffers'
import { $neutralGray70 } from '../../../../styles/colors/primitives'
import { CircularProgress } from '@mui/material'
import Text from '../../../Text'
import Subtotal from '../ProductCheckoutPage/Subtotal'
import {
  calculatePriceForMultipleShopItems,
  calculateDiscountFromRegularPriceCents,
} from '../../../../utils/shopItemsAndCart'
import SecondaryNavbarAddToCart from '../ProductCheckoutPage/SecondaryNavbarAddToCart'
import CartIcon from '../../../icons/CartIcon'
import ProductInfoTags, { RIG_TRANSIT_TIME_DAYS } from '../ProductCheckoutPage/ProductInfoTags'
import Carousel from '../ProductCheckoutPage/Carousel'
import { findItemByType, isOutOfStock, isUnavailableInRegion } from '../../../../utils/shopItems'
import CheckmarkIcon from '../../../icons/CheckmarkIcon'
import Description from '../ProductCheckoutPage/Description'
import SimpleBlockContent from '../../../SimpleBlockContent'
import ProductImages from '../ProductCheckoutPage/ProductImages'
import SavingsTable from './SavingsTable'
import { addCentsToPrice } from '../../../../utils/prices'
import { DEFAULT_MOUNT, resolveMountOptions } from '../../../../lib/gt2Mount'

const cx = (...c) => c.filter(Boolean).join(' ')

const PRODUCT_SET_ID = 'gt2-full-rig-bundle'

// Full-rig bundle checkout: one product, a cockpit-colour picker. Each colour is
// its own backend bundle offer (a shopItem sharing productSetId
// 'gt2-full-rig-bundle' + colorInfo); picking a colour swaps the resolved bundle
// item so the chosen colour actually ships (VRS ships colour by ItemType) and its
// own gallery renders. No seat/monitor/accessory toggles — the bundle already
// includes everything. Mirrors the cockpit configurator's colour→item→cart
// plumbing (same swatch UI), minus the configuration controls.
//
// NOTE: this page deliberately does NOT go through getItemOffers({
// requireDisplayOnShop: true }) the way ProductCheckoutPage does. The bundle is
// unlisted (displayOnShop false on every colour, so it stays out of the /shop
// grid) and that filter would drop it entirely — which is exactly why the old
// ProductCheckoutPage-backed version of this page rendered an empty gallery and a
// "Get notified" button instead of a buy box.
function GT2FullRigCheckoutPage({ title, description, importantInfo }) {
  const shoppingCart = useCartStore(selectCart)
  const openCart = useUiStore((s) => s.openCart)
  const { checkoutPageAddToCart } = useAddToCart()

  // Only the client-side full-offers refetch gate is shared (like the cockpit fork);
  // the item is derived from the selected colour, not a fixed itemType.
  const { offers: shopItems, shopItemsFetched } = useProductOffers()

  // Only colours the visitor can actually BUY here. The catalog is global, so all
  // seven colour docs are in `shopItems` in every region; a colour whose backend
  // bundle offer this region did not return is stamped UNAVAILABLE with a null
  // price by the catalog join. Filtering on that (not just stock) is what keeps
  // the swatch row honest in two live situations: on /us and /au, where the rig is
  // EU-only, and in the window before the backend's six colour bundle offers
  // deploy, where only Black resolves and the other swatches would otherwise blank
  // the whole page when clicked.
  const availableColors = useMemo(() => (shopItems || [])
    .filter(item => item.productSetId === PRODUCT_SET_ID && item.colorInfo)
    .filter(item => !isUnavailableInRegion(item))
    .map(item => ({
      colorName: item.colorInfo.colorName,
      colorHex: item.colorInfo.colorHex,
      itemType: item.type,
      isOutOfStock: isOutOfStock(item),
    })), [shopItems])

  const [selectedColor, setSelectedColor] = useState(null)

  // Wheelbase mount, identical model to the cockpit configurator: same price,
  // carried as a zero-cost kit line so the order says which plate to pack.
  // Empty (and the control hidden) until the backend serves the kit offers.
  const mountOptions = useMemo(() => resolveMountOptions(shopItems), [shopItems])
  const [selectedMount, setSelectedMount] = useState(DEFAULT_MOUNT)

  useEffect(() => {
    if (availableColors.length > 0 && !selectedColor) {
      const inStock = availableColors.find(c => !c.isOutOfStock)
      setSelectedColor(inStock?.colorName || availableColors[0].colorName)
    }
  }, [availableColors, selectedColor])

  useEffect(() => {
    if (mountOptions.length > 0 && !mountOptions.some(m => m.id === selectedMount)) {
      setSelectedMount(mountOptions[0].id)
    }
  }, [mountOptions, selectedMount])

  const getRigItem = () => {
    const cfg = availableColors.find(c => c.colorName === selectedColor)
    if (!cfg) return null
    return findItemByType(cfg.itemType, shopItems)
  }

  // No purchasable colour in this region — the EU-only guard. It has to come
  // BEFORE the loading gate below: with an empty colour list `selectedColor` never
  // gets set, so gating on it alone would spin forever on /us and /au.
  if (shopItemsFetched && availableColors.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={cx(styles.contentWrapper, styles.unavailable)}>
          <Text type="heading-4">Product not available in your region.</Text>
        </div>
      </div>
    )
  }

  if (!shopItemsFetched || !selectedColor) {
    return (
      <div className={styles.loading} style={{ color: $neutralGray70 }}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  const rigItem = getRigItem()

  if (!rigItem) {
    return (
      <div className={styles.wrapper}>
        <div className={cx(styles.contentWrapper, styles.unavailable)}>
          <Text type="heading-4">Product not available in your region.</Text>
        </div>
      </div>
    )
  }

  const images = rigItem.galleryImages || []
  const selectedMountOption = mountOptions.find(m => m.id === selectedMount) || null
  const mountKitItem = selectedMountOption?.item || null
  const selectedItems = mountKitItem ? [rigItem, mountKitItem] : [rigItem]
  const subtotal = calculatePriceForMultipleShopItems(selectedItems)
  const discountCents = calculateDiscountFromRegularPriceCents(selectedItems)
  const subtotalBeforeDiscount = addCentsToPrice(subtotal, discountCents)
  const itemsInCart = selectedItems.length > 0 && selectedItems.every(item => shoppingCart[item.id])
  const selectedColorConfig = availableColors.find(c => c.colorName === selectedColor)

  const onAddToCartClick = () => {
    checkoutPageAddToCart(selectedItems.map(i => i.id))
  }

  const getAddToCartCta = (isMainCta) => ({
    title: itemsInCart ? 'Item added' : 'Add to cart',
    titleIcon: itemsInCart ? <CheckmarkIcon/> : (isMainCta ? <CartIcon large/> : <CartIcon/>),
    light: itemsInCart,
    sx: {
      height: isMainCta ? '80px' : null,
      titleTextType: isMainCta ? 'large' : null,
      width: isMainCta ? '100%' : null,
    },
    disabled: selectedItems.length === 0 || isOutOfStock(rigItem),
    onClick: () => {
      if (itemsInCart) {
        openCart()
      } else {
        onAddToCartClick()
      }
    },
  })

  const heading = title || 'GT-2 Full Rig Bundle'

  return (
    <>
      <SecondaryNavbarAddToCart
        title={heading}
        subtotal={subtotal}
        subtotalBeforeDiscount={subtotalBeforeDiscount}
        ctaV2={getAddToCartCta(false)}
      />
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          {/* Gallery + savings table share the left column so the table sits
              directly UNDER the gallery on desktop. On mobile the whole column is
              ordered after the buy box (see styles), so the table follows the
              price instead of pushing it below the fold. */}
          <div className={styles.leftColumn}>
            <div className={styles.desktopCarousel}>
              {/* Keyed on the colour: ProductImages keys its slots by image src,
                  and remounting on colour change stops the previous colour's
                  frame lingering in slot 1 (the black-first-image bug class). */}
              <ProductImages key={selectedColor} images={images}/>
            </div>
            <SavingsTable bundleItem={rigItem} shopItems={shopItems}/>
          </div>
          <div className={styles.productInfo}>
            <div className={styles.productText}>
              <div className={styles.productTitle}>
                <Text type="heading-10">VRS® DirectForce®</Text>
                <Text type="heading-3" primary>{heading}</Text>
              </div>
              {description && (Array.isArray(description)
                ? <Description overrideDescription={description}/>
                : <Text type="body">{description}</Text>)}
              {importantInfo && <SimpleBlockContent blocks={importantInfo}/>}
            </div>
            <div className={styles.mobileCarousel}><Carousel key={selectedColor} images={images}/></div>
            <div className={styles.checkout}>
              {availableColors.length > 0 && (
                <div className={styles.configSection}>
                  <div className={styles.configSectionTitle}>
                    <Text type="heading-8">Cockpit colour</Text>
                  </div>
                  <div className={styles.colorSwatchesWrapper}>
                    {availableColors.map(color => (
                      <button
                        key={color.colorName}
                        className={cx(
                          styles.colorSwatch,
                          selectedColor === color.colorName && styles.colorSwatchSelected,
                          color.isOutOfStock && styles.colorSwatchDisabled
                        )}
                        style={{ '--swatch-color': color.colorHex }}
                        onClick={() => !color.isOutOfStock && setSelectedColor(color.colorName)}
                        aria-label={color.colorName}
                        aria-pressed={selectedColor === color.colorName}
                        disabled={color.isOutOfStock}
                      />
                    ))}
                    <Text type="small">
                      {selectedColor}{selectedColorConfig?.isOutOfStock && ' (Out of stock)'}
                    </Text>
                  </div>
                </div>
              )}

              {/* Wheelbase mount. Same price either way; it picks the mounting
                  plate that ships with the chassis and adds a zero-cost kit line
                  so the order says which one to pack. */}
              {mountOptions.length > 1 && (
                <div className={styles.configSection}>
                  <div className={styles.configSectionTitle}>
                    <Text type="heading-8">Wheelbase mount</Text>
                  </div>
                  <select
                    className={styles.mountSelect}
                    value={selectedMount}
                    aria-label="Wheelbase mount"
                    onChange={(e) => setSelectedMount(e.target.value)}
                  >
                    {mountOptions.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                  <div className={styles.mountHint}>
                    <Text type="small">
                      Choose how your wheel base mounts to the chassis. Your rig ships with the mount you select here. The price is the same either way.
                    </Text>
                  </div>
                </div>
              )}

              <div className={styles.subtotalAndPreOrder}>
                <Subtotal
                  subtotal={subtotal}
                  discountCents={discountCents}
                  taxIncluded={rigItem?.taxIncluded}
                  cta={getAddToCartCta(true)}
                />
              </div>

              {/* Shares the single rig estimate with the configurator and the
                  product page's rig branch, so the three cannot drift apart. */}
              <ProductInfoTags transitTimeDays={RIG_TRANSIT_TIME_DAYS}/>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default GT2FullRigCheckoutPage
